import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux';
import {Affix, Alert, Divider, Segmented, Select} from 'antd';
import {Helmet} from 'react-helmet';
import {useCookies} from "react-cookie";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from '@fortawesome/free-solid-svg-icons';
import gsap from 'gsap';

import classes from './CommonSchedule.module.scss'
import {scheduleShouldBeClosedDueToItsDevelopment} from '../../../services/scheduleService';

import {checkCyrillicLetters, formatFio} from '../../../services/generalService';
import {
    getCurrentWeekNumber,
    getEmployeeAnnouncements,
    getEmployees,
    getEmployeeSchedule,
    getGroupAnnouncements,
    getGroupsByName,
    getGroupsByNameSuccess,
    getGroupSchedule,
    setEmployees,
    setWeekFilters
} from '../../../store/actions';

import Spinner from '../../UI/Spinner/Spinner';

import data from '../../../assets/data/meta.json'
import UnderlinedLink from "../../UI/UnderlinedLink/UnderlinedLink";
import AdvancedSearch from "../AdvancedScheduleSearch/AdvancedSearch";
import {isVisible} from "bootstrap/js/src/util";
import ScheduleFilteringView from './ScheduleFilteringView';
import {ViewOption} from "./ScheduleView";

const { Option } = Select;


const CommonSchedule = (props) => {

    const isClosedDueToDevelopment = scheduleShouldBeClosedDueToItsDevelopment();

    const [scheduleType, setScheduleType] = useState(0); // 0 - empty, 1 - group, 2 - employee
    const [urlId, setUrlId] = useState(null);
    const [selectedScheduleTarget, setSelectedScheduleTarget] = useState(null);
    const [searchingValue, setSearchingValue] = useState('');
    const [metaData, setMetaData] = useState({ title: data[17].title, description: data[17].description });

    const dispatch = useDispatch();
    const [scheduleViewCookies, setScheduleViewCookies] = useCookies(["scheduleView"]);
    const employees = useSelector(state => state.schedule.employees);
    const studentGroups = useSelector(state => state.schedule.studentGroups);
    const currentWeekNumber = useSelector(state => state.schedule.currentWeekNumber);
    const loadingCurrentWeekNumber = useSelector(state => state.schedule.loadingCurrentWeekNumber)
    const employeeSchedule = useSelector(state => state.schedule.employeeSchedule);
    const loadingEmployeeSchedule = useSelector(state => state.schedule.loadingEmployeeSchedule);
    const employeeAnnouncements = useSelector(state => state.schedule.employeeAnnouncements);
    const loadingEmployeeAnnouncements = useSelector(state => state.schedule.loadingEmployeeAnnouncements);
    const groupSchedule = useSelector(state => state.schedule.groupSchedule);
    const loadingGroupSchedule = useSelector(state => state.schedule.loadingGroupSchedule);
    const groupAnnouncements = useSelector(state => state.schedule.groupAnnouncements);
    const loadingGroupAnnouncements = useSelector(state => state.schedule.loadingGroupAnnouncements);
    const [timeoutIdForData, setTimeoutIdForData] = useState(null); // сетится, чтобы стриггерить useEffect`ы с загрузкой информации
    const [advancedSearchIsOpen, setAdvancedSearchIsOpen] = useState(false);
    const isSunday = useSelector(state => state.schedule.isSunday);

    const searchBoxRef = useRef(null);

    const [activeScheduleView, setActiveScheduleView] = useState(getInitialScheduleView);
    const viewOptions = [
        { label: "Таблица", value: ViewOption.Table },
        { label: "Список", value: ViewOption.List },
    ];

    function getInitialScheduleView() {
        if (scheduleViewCookies.scheduleView) {
            return Number(scheduleViewCookies.scheduleView);
        }
        return ViewOption.Table;
    }

    const getNextUpdateTime = () => {
        const tomorrowDate = new Date();
        tomorrowDate.setDate(tomorrowDate.getDate() + 1);
        tomorrowDate.setHours(0, 0, 0, 1);
        return tomorrowDate.getTime();
    }

    useEffect(() => {
        const context = gsap.context(() => {
            gsap.to(`.${classes.searchBarStickyContainer}`, {
                boxShadow: '0 15px 19px -9px #b0b0b0',
                duration: 0.3,
                scrollTrigger: {
                    trigger: `.${classes.divider}`,
                    scrub: 1,
                    ease: 'none',
                    start: 'top top',
                    end: 'top top',
                }
            });
            gsap.to(`.${classes.searchBarStickyContainer}`, {
                background: '#E9EDF3',
                duration: 0.3,
                scrollTrigger: {
                    trigger: `.${classes.divider}`,
                    ease: 'none',
                    start: 'top top',
                    end: 'top top',
                }
            });
        });
        return () => context.kill();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setTimeoutIdForData(timeoutId);
        }, getNextUpdateTime() - (new Date()).getTime());

        const killTimeoutForData = (timeoutIdToKill) => {
            clearTimeout(timeoutIdToKill);
        }
        return () => killTimeoutForData(timeoutId); // чтобы не спамить событиями после смены отрисовываемого компонента
    }, [timeoutIdForData]);

    const onScheduleUrlChange = () => {
        let url = null;
        if (props.match.params.id) {
            url = props.match.params.id;
        } else if (localStorage.getItem('scheduleUrlId')) {
            url = JSON.parse(localStorage.getItem('scheduleUrlId'));
        }

        if (url) {
            setUrlId(url);
            if (/\d/.test(url)) setScheduleType(1);
            else setScheduleType(2);
        }
    }

    useEffect(() => {
        onScheduleUrlChange();
    }, [props.match.params.id]);

    useEffect(() => {
        dispatch(getCurrentWeekNumber());
    }, [dispatch, timeoutIdForData]);

    const setEmployeeDegreeAndRank = useCallback(() => {
        let employeeDetails = formatFio(selectedScheduleTarget);

        let degreeAndRankArray = [];
        if (selectedScheduleTarget.degreeAbbrev) degreeAndRankArray.push(selectedScheduleTarget.degreeAbbrev);
        if (selectedScheduleTarget.rank) degreeAndRankArray.push(selectedScheduleTarget.rank);

        return employeeDetails + (degreeAndRankArray.length > 0 ? ` (${degreeAndRankArray.join(', ')})` : '');
    }, [selectedScheduleTarget]);

    useEffect(() => {
        if (selectedScheduleTarget) {
            if (scheduleType === 1 && selectedScheduleTarget.name) setMetaData({
                title: data[2].title.replace('XXXXXX', selectedScheduleTarget.name),
                description: data[2].description.replace('XXXXXX', selectedScheduleTarget.name)
            });
            if (scheduleType === 2 && selectedScheduleTarget.lastName) {
                const employeeDetails = setEmployeeDegreeAndRank();
                setMetaData({
                    title: `${employeeDetails} ${data[4].title}`,
                    description: `${employeeDetails} ${data[4].description}`
                });
            }
        }
    }, [scheduleType, selectedScheduleTarget, setEmployeeDegreeAndRank]);

    useEffect(() => {
        if (isClosedDueToDevelopment) return;

        if (urlId) {
            localStorage.setItem('scheduleUrlId', JSON.stringify(urlId));
            if (scheduleType === 1) {
                dispatch(getGroupSchedule(urlId))
                    .then(groupSchedule => setSelectedScheduleTarget(groupSchedule.studentGroupDto));
                dispatch(getGroupAnnouncements(urlId));
            }
            if (scheduleType === 2) {
                dispatch(getEmployeeSchedule(urlId))
                    .then(employeeSchedule => setSelectedScheduleTarget(employeeSchedule.employeeDto));
                dispatch(getEmployeeAnnouncements(urlId));
            }
        }
    }, [dispatch, scheduleType, urlId, props.history, timeoutIdForData, isClosedDueToDevelopment]);

    const getSearchBoxValue = () => {
        if (selectedScheduleTarget) {
            if (scheduleType === 1) return selectedScheduleTarget.name;
            if (scheduleType === 2 && selectedScheduleTarget.lastName) {
                return formatFio(selectedScheduleTarget);
            }
        }
        return null;
    }

    const onSearchBoxSearch = (searchValue) => {
        setSearchingValue(searchValue);
        if (searchValue.length >= 2 && searchValue.trim().length !== 0) {
            if (/\d/.test(searchValue)) dispatch(getGroupsByName(searchValue));
            if (!/\d/.test(searchValue)) dispatch(getEmployees(checkCyrillicLetters(searchValue)));
        } else {
            if (scheduleType === 1) dispatch(setEmployees([]));
            if (scheduleType === 2) dispatch(getGroupsByNameSuccess([]));
        }
    }

    const onSearchBoxChange = (option) => {
        if (option) {
            const tmpWeeks = [false, false, false, false];
            tmpWeeks[currentWeekNumber - 1 + Number(isSunday)] = true;
            dispatch(setWeekFilters(tmpWeeks));

            let newSearchValue = null;

            if (/\d/.test(searchingValue)) {
                newSearchValue = studentGroups.find(item => item.id.toString() === option.value);
                setSelectedScheduleTarget(newSearchValue);
                props.history.replace({ pathname: `/schedule/${newSearchValue.name}` });
            }
            if (!/\d/.test(searchingValue)) {
                newSearchValue = employees.find(item => item.id.toString() === option.value);
                setSelectedScheduleTarget(newSearchValue);
                props.history.replace({ pathname: `/schedule/${newSearchValue.urlId}` });
            }
        } else {
            setSelectedScheduleTarget(null);
        }
        if (searchBoxRef) {
            searchBoxRef.current.blur();
        }
    }

    const getScheduleErrorAlert = (description) => {
        return (<Alert
            message="Ошибка!"
            description={description}
            type="error"
            showIcon
            className={classes.alertContainer}
        />);
    }

    const isScheduleEmpty = () => {
        const schedule = scheduleType === 1 ? groupSchedule : employeeSchedule;
        if (!schedule) return false;
        return (!schedule.schedules || schedule.schedules.length === 0)
            && (!schedule.previousSchedules || schedule.previousSchedules.length === 0)
            && (!schedule.exams || schedule.exams.length === 0);
    }

    const checkScheduleState = () => {
        if (scheduleType !== 0) {
            if ((scheduleType === 1 && groupSchedule instanceof Error && !loadingGroupSchedule) ||
                (scheduleType === 2 && employeeSchedule instanceof Error && !loadingEmployeeSchedule)) {
                return getScheduleErrorAlert('Расписание не найдено.');
            } else if (!currentWeekNumber ||
                (scheduleType === 1 && (loadingGroupSchedule || Object.keys(groupSchedule ?? {}).length === 0 || loadingGroupAnnouncements || !groupAnnouncements)) ||
                (scheduleType === 2 && (loadingEmployeeSchedule || Object.keys(employeeSchedule ?? {}).length === 0 || loadingEmployeeAnnouncements || !employeeAnnouncements))) {
                return <Spinner />;
            } else if (isScheduleEmpty()) {
                return getScheduleErrorAlert('Расписание не добавлено.');
            }
            return getScheduleAndControls();
        }
        return null;
    }

    const setViewOptionToCookies = (activeView) => {
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 1);

        setScheduleViewCookies('scheduleView', activeView, { path: '/', expires: expirationDate });
    }

    const getScheduleAndControls = () => {
        let currentLessons = null;
        let previousLessons = null;
        let scheduleTargetDto;

        if (scheduleType === 1) {
            scheduleTargetDto = {
                ...groupSchedule.studentGroupDto,
                startDate: groupSchedule.startDate,
                endDate: groupSchedule.endDate,
                startExamsDate: groupSchedule.startExamsDate,
                endExamsDate: groupSchedule.endExamsDate,
                announcements: groupAnnouncements,
                loadingAnnouncements: loadingGroupAnnouncements || !groupAnnouncements
            };
        } else {
            scheduleTargetDto = {
                ...employeeSchedule.employeeDto,
                startDate: employeeSchedule.startDate,
                endDate: employeeSchedule.endDate,
                startExamsDate: employeeSchedule.startExamsDate,
                endExamsDate: employeeSchedule.endExamsDate,
                announcements: employeeAnnouncements,
                loadingAnnouncements: loadingEmployeeAnnouncements || !employeeAnnouncements
            };
        }
        const targetSchedule = scheduleType === 1 ? groupSchedule : employeeSchedule;
        if (targetSchedule.schedules) {
            currentLessons = {
                schedules: targetSchedule.schedules,
                term: targetSchedule.currentTerm
            };
        }
        if (targetSchedule.previousSchedules) {
            previousLessons = {
                schedules: targetSchedule.previousSchedules,
                term: targetSchedule.previousTerm
            };
        }
        const currentPeriod = targetSchedule.currentPeriod;
        const exams = targetSchedule.exams;

        const propsToPass = {
            activeView: activeScheduleView,
            scheduleTarget: scheduleTargetDto,
            scheduleType,
            currentTermLessons: currentLessons,
            previousTermLessons: previousLessons,
            currentPeriod,
            exams
        }
        return <ScheduleFilteringView {...propsToPass} />;
    }

    const getDispatcherPhones = () => {
        return (<div style={{ fontSize: '13px' }} className='Schedule_details_color'>
            <Divider style={{ margin: '16px 0' }} />
            <div>
                {`Тел. диспетчера: `}
                <UnderlinedLink href='tel:+375172938933' text='+375 (17) 293-89-33' />{`, `}
                <UnderlinedLink href='tel:+375172938044' text='+375 (17) 293-80-44' />{`, `}
                <UnderlinedLink href='tel:+375172938034' text='+375 (17) 293-80-34' />
            </div>
        </div>);
    }

    const onGroupSelected = (group) => {
        const groupUnwrapped = group.studentGroup;
        props.history.replace({ pathname: `/schedule/${groupUnwrapped.name}` });
        setAdvancedSearchIsOpen(false);
    }

    const popupStateChangeHandler = () => {
        const allowedClassnames = ['ant-select-item-option-content'];

        const clickOrScrollListenerCreator = () => (e) => {

            const popupElements = document.querySelectorAll('.ant-dropdown.ant-dropdown-placement-bottomRight');
            if (!advancedSearchIsOpen || popupElements.length === 0) {
                return;
            }
            const popupElement = popupElements[popupElements.length - 1];
            let element = null;
            let isAllowedClassname = false;
            if (typeof e.target.className === 'string' && e.target.className) {
                element = popupElement.querySelector(`.${e.target.className}`);
                isAllowedClassname = allowedClassnames.some(cl => e.target.className.includes(cl));
            }
            if (!element && isVisible(popupElement) && !isAllowedClassname) {
                setAdvancedSearchIsOpen(false);
            }
            removeListeners();
        }
        const clickListener = clickOrScrollListenerCreator();
        const scrollListener = clickOrScrollListenerCreator();

        const removeListeners = () => {
            document.removeEventListener('click', clickListener);
            document.removeEventListener('scroll', scrollListener);
        }
        document.addEventListener('click', clickListener);
        document.addEventListener('scroll', scrollListener);
    }

    const getSearchBarElement = () => {
        const searchInput = (<AdvancedSearch
            onGroupSelected={onGroupSelected}
            visible={advancedSearchIsOpen}
            onVisibleChange={popupStateChangeHandler}>
            <Select
                labelInValue
                className={classes.searchInput}
                showSearch={true}
                placeholder="Введите ФИО преподавателя или номер группы"
                suffixIcon={
                    <span style={{ display: 'flex', columnGap: '10px' }}>
                        <FontAwesomeIcon style={{ pointerEvents: 'auto' }}
                            icon={faSearch} />
                    </span>}
                filterOption={false}
                notFoundContent={searchingValue && searchingValue.length > 1 ? 'Не найдено' : null}
                value={getSearchBoxValue()}
                onSearch={(searchValue) => onSearchBoxSearch(searchValue)}
                onChange={(value) => onSearchBoxChange(value)}
                ref={searchBoxRef}>
                {
                    /\d/.test(searchingValue)
                        ? studentGroups.map((group) =>
                            <Option key={group.id}>
                                {`${group.name} `}
                                {group.specialityAbbrev && `(${group.specialityAbbrev})`}
                            </Option>)
                        : employees.map((employee) =>
                            <Option key={employee.id}>
                                {`${formatFio(employee)} `}
                                {employee.academicDepartment && `(${employee.academicDepartment})`}
                            </Option>)
                }
            </Select>
        </AdvancedSearch>);

        // TODO: what is this?
        return (<Fragment>
            <Affix className={classes.test1}>
                <div className={classes.searchBarStickyContainer}>
                    {searchInput}
                </div>
            </Affix>

            <div className={classes.test2}>
                {searchInput}
            </div>
        </Fragment>);
    }

    return (<div className={classes.layout}>
        <Helmet>
            <title>{metaData.title}</title>
            <meta
                name="description"
                content={metaData.description} />
        </Helmet>

        {isClosedDueToDevelopment ?
            <div className={classes.scheduleIsClosedMessage}>
                <div>На данный момент расписание недоступно, идёт формирование расписания на следующий семестр.</div>
                <div className={classes.closedMessageBottomPart}>Расписание будет доступно после 15 августа.</div>
            </div> :
            <Fragment>
                <div className={classes.headerSection}>
                    <div className={classes.scheduleHeaderContainer}>
                        <div className={classes.header}>
                            <h4>Расписание занятий БГУИР</h4>
                            <Segmented className={classes.tabs}
                                options={viewOptions}
                                onChange={(activeKey) => {
                                    setViewOptionToCookies(activeKey);
                                    setActiveScheduleView(activeKey);
                                }}
                                value={activeScheduleView} />
                        </div>
                        <Divider className={classes.divider} />

                        <div className={classes.searchBar}>
                            {getSearchBarElement()}

                            <div className={classes.subtitle}>
                                <span className={classes.week}>
                                    {(!loadingCurrentWeekNumber && currentWeekNumber) &&
                                        currentWeekNumber + '-я учебная неделя'}
                                </span>
                            </div>
                        </div>

                        {(!selectedScheduleTarget || isScheduleEmpty()) && getDispatcherPhones()}
                    </div>
                </div>

                <div className={classes.contentSection}>
                    {checkScheduleState()}
                    {(selectedScheduleTarget && !isScheduleEmpty()) && getDispatcherPhones()}
                </div>
            </Fragment>}
    </div>);
}

export default CommonSchedule;
